import React, { useState, Component } from 'react';
import { Card } from '../../SubscriptionAppCard';
import { Input } from '../../SubscriptionAppInput';
import { User, Mail, Phone, DollarSign, TrendingDown, Copy, Check } from 'lucide-react';
// Inline CopyButton component
function CopyButton({
  onClick,
  copied,
  ariaLabel
}: {
  onClick: () => void;
  copied: boolean;
  ariaLabel: string;
}) {
  return <button onClick={onClick} className="p-1.5 hover:bg-[#F1F5F9] rounded transition-colors" aria-label={ariaLabel} data-id="element-2754">
      {copied ? <Check className="w-4 h-4 text-[#10B981]" data-id="element-2755" /> : <Copy className="w-4 h-4 text-[#64748B]" data-id="element-2756" />}
    </button>;
}
// Inline InfoRow component
function InfoRow({
  icon: Icon,
  value,
  valueColor = '#475569',
  valueFontWeight = 'normal',
  iconColor = '#64748B',
  action
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  value: React.ReactNode;
  valueColor?: string;
  valueFontWeight?: 'normal' | 'medium' | 'semibold';
  iconColor?: string;
  action?: React.ReactNode;
}) {
  const fontWeightClass = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold'
  }[valueFontWeight];
  return <div className="flex items-center justify-between gap-3" data-id="element-2757">
      <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-2758">
        <Icon className="w-5 h-5 flex-shrink-0" style={{
        color: iconColor
      }} data-id="element-2759" />
        <span className={`text-[14px] leading-[1.5] ${fontWeightClass}`} style={{
        color: valueColor
      }} data-id="element-2760">
          {value}
        </span>
      </div>
      {action && <div className="flex-shrink-0" data-id="element-2761">{action}</div>}
    </div>;
}
// Inline Divider component
function Divider() {
  return <div className="border-t border-[#E2E8F0]" data-id="element-2762" />;
}
// Email validation function
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
// International phone validation function
// Accepts international format with country code (e.g., +61 4XX XXX XXX, +1 555-123-4567)
// This is a simplified validation that checks for:
// - Starts with + followed by 1-3 digits (country code)
// - Followed by 6-15 digits (with optional spaces, dashes, or parentheses)
function isValidInternationalPhone(phone: string): boolean {
  // Remove all spaces, dashes, and parentheses for validation
  const cleanPhone = phone.replace(/[\s\-()]/g, '');
  // Check if it starts with + and has valid format
  // Country code (1-3 digits) + subscriber number (6-15 digits)
  const internationalPhoneRegex = /^\+\d{1,3}\d{6,15}$/;
  return internationalPhoneRegex.test(cleanPhone);
}
// Helper to format Australian phone numbers as user types
function formatAustralianPhone(value: string): string {
  // Remove all non-digit characters except +
  const cleaned = value.replace(/[^\d+]/g, '');
  // If it starts with +61 (Australia), format it nicely
  if (cleaned.startsWith('+61')) {
    const digits = cleaned.substring(3); // Remove +61
    if (digits.length <= 1) return '+61 ' + digits;
    if (digits.length <= 4) return '+61 ' + digits.substring(0, 1) + ' ' + digits.substring(1);
    if (digits.length <= 7) return '+61 ' + digits.substring(0, 1) + ' ' + digits.substring(1, 4) + ' ' + digits.substring(4);
    return '+61 ' + digits.substring(0, 1) + ' ' + digits.substring(1, 4) + ' ' + digits.substring(4, 7) + (digits.length > 7 ? ' ' + digits.substring(7, 10) : '');
  }
  return cleaned;
}
interface CustomerSummaryProps {
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  totalPaid: string;
  totalSaved: string;
  isEditing?: boolean;
  onEmailChange?: (email: string) => void;
  onPhoneChange?: (phone: string) => void;
  'data-id'?: string;
}
export function CustomerSummary({
  customerName,
  customerEmail,
  customerPhone = '+61 4XX XXX XXX',
  totalPaid,
  totalSaved,
  isEditing = false,
  onEmailChange,
  onPhoneChange,
  'data-id': dataId
}: CustomerSummaryProps) {
  const [emailCopied, setEmailCopied] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(customerEmail);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };
  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(customerPhone);
      setPhoneCopied(true);
      setTimeout(() => setPhoneCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy phone:', err);
    }
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    onEmailChange?.(newEmail);
    // Validate email
    if (newEmail && !isValidEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    // Format if it looks like an Australian number
    const formatted = formatAustralianPhone(newPhone);
    onPhoneChange?.(formatted);
    // Validate phone
    if (newPhone && !isValidInternationalPhone(newPhone)) {
      setPhoneError('Please enter a valid phone number with country code (e.g., +61 4XX XXX XXX)');
    } else {
      setPhoneError('');
    }
  };
  return <Card padding="none" data-id="element-2763">
      <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-2764">
        <h3 className="text-[20px] font-semibold text-[#0F172A] leading-[1.4]" data-id="element-2765">
          Customer
        </h3>
      </div>

      <div className="p-6 space-y-5" data-id="element-2766">
        {/* Customer Name */}
        <InfoRow icon={User} value={customerName} valueColor="#0F172A" valueFontWeight="medium" data-id="element-2767" />

        {/* Customer Email */}
        {isEditing ? <div className="flex items-start gap-3" data-id="element-2768">
            <Mail className="w-5 h-5 flex-shrink-0 text-[#64748B] mt-2.5" data-id="element-2769" />
            <div className="flex-1" data-id="element-2770">
              <Input type="email" value={customerEmail} onChange={handleEmailChange} placeholder="customer@example.com" error={emailError} data-id="element-2771" />
            </div>
          </div> : <InfoRow icon={Mail} value={<span className="break-all" data-id="element-2773">{customerEmail}</span>} action={<CopyButton onClick={handleCopyEmail} copied={emailCopied} ariaLabel="Copy email address" data-id="element-2774" />} data-id="element-2772" />}

        {/* Customer Phone */}
        {isEditing ? <div className="flex items-start gap-3" data-id="element-2775">
            <Phone className="w-5 h-5 flex-shrink-0 text-[#64748B] mt-2.5" data-id="element-2776" />
            <div className="flex-1" data-id="element-2777">
              <Input type="tel" value={customerPhone} onChange={handlePhoneChange} placeholder="+61 4XX XXX XXX" error={phoneError} data-id="element-2778" />
            </div>
          </div> : <InfoRow icon={Phone} value={customerPhone} action={<CopyButton onClick={handleCopyPhone} copied={phoneCopied} ariaLabel="Copy phone number" data-id="element-2780" />} data-id="element-2779" />}

        <Divider data-id="element-2781" />

        {/* Total Paid */}
        <div className="flex items-center justify-between" data-id="element-2782">
          <InfoRow icon={DollarSign} value="Total paid" data-id="element-2783" />
          <span className="text-[14px] font-semibold text-[#0F172A] leading-[1.5]" data-id="element-2784">
            {totalPaid}
          </span>
        </div>

        {/* Total Saved */}
        <div className="flex items-center justify-between" data-id="element-2785">
          <InfoRow icon={TrendingDown} iconColor="#10B981" value="Total saved" data-id="element-2786" />
          <span className="text-[14px] font-semibold text-[#10B981] leading-[1.5]" data-id="element-2787">
            {totalSaved}
          </span>
        </div>
      </div>
    </Card>;
}
export type { CustomerSummaryProps };